<template>
  <div class="min-h-screen  flex font-sans mt-14">
    <Sidebar />

    <div class="flex-1 p-4 md:p-8 text-white overflow-y-auto">
      
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <h1 class="text-2xl font-black text-white tracking-tight">تنظیمات مالی و ارزها</h1>
        
        <div class="bg-[#1a2220] p-1 rounded-xl flex ring-1 ring-white/10">
          <button 
            @click="activeTab = 'status'"
            class="px-6 py-2 rounded-lg text-sm font-bold transition-all duration-300"
            :class="activeTab === 'status' ? 'bg-[#b9f1cf] text-[#0a1f14] shadow-lg shadow-[#b9f1cf]/20' : 'text-white/60 hover:text-white'"
          >
            وضعیت ارزها
          </button>
          <button 
            @click="activeTab = 'fees'"
            class="px-6 py-2 rounded-lg text-sm font-bold transition-all duration-300"
            :class="activeTab === 'fees' ? 'bg-[#b9f1cf] text-[#0a1f14] shadow-lg shadow-[#b9f1cf]/20' : 'text-white/60 hover:text-white'"
          >
            کارمزدها و درصدها
          </button>
        </div>
      </div>

      <div v-if="activeTab === 'status'" class="animate-fade-in">
        <div class="rounded-3xl bg-gradient-to-b from-[#1a2220]/90 to-[#12181b]/90 border border-[#334D44]/50 backdrop-blur-xl p-6 shadow-2xl">
          
          <div v-if="loading" class="flex flex-col items-center justify-center py-20 text-white/50 gap-4">
             <div class="animate-spin h-10 w-10 border-2 border-[#b9f1cf] rounded-full border-t-transparent"></div>
             <span>در حال بارگذاری لیست ارزها...</span>
          </div>

          <div v-else class="grid gap-6">
            <div v-for="item in filteredPrices" :key="item._id" class="bg-[#0f1417]/40 border border-[#334D44]/30 rounded-2xl p-5 transition-all hover:border-[#b9f1cf]/30 hover:bg-[#0f1417]/60 group">
              
              <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6 border-b border-[#334D44]/30 pb-4">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#b9f1cf]/20 to-[#1a2220] flex items-center justify-center text-[#b9f1cf] font-bold text-lg ring-1 ring-[#b9f1cf]/20 shadow-lg shadow-[#b9f1cf]/5 group-hover:scale-110 transition-transform">
                      {{ (item.name || '').substring(0, 2).toUpperCase() }}
                  </div>
                  <div>
                      <div class="font-bold text-xl text-white tracking-wide">{{ item.name }}</div>
                      <div class="text-xs text-white/40 font-mono mt-1 flex items-center gap-2">
                        <span>پایه: {{ item.price?.toLocaleString() }} T</span>
                        <span class="w-1 h-1 rounded-full bg-white/20"></span>
                        <span class="opacity-70">ID: {{ item._id.slice(-4) }}</span>
                      </div>
                  </div>
                </div>

                <div class="flex-1 grid grid-cols-2 md:grid-cols-3 gap-2 bg-[#1a2220]/50 p-3 rounded-xl border border-white/5">
                    <div class="flex flex-col items-center justify-center border-l border-white/5 pl-2">
                        <span class="text-[10px] text-white/50 mb-1">قیمت کاربر (خرید)</span>
                        <span class="text-sm font-bold text-[#b9f1cf]">{{ calculatePrice(item, 'user').toLocaleString() }}</span>
                    </div>
                    <div class="flex flex-col items-center justify-center border-l border-white/5 pl-2">
                        <span class="text-[10px] text-white/50 mb-1">قیمت همکار (خرید)</span>
                        <span class="text-sm font-bold text-blue-300">{{ calculatePrice(item, 'coworker').toLocaleString() }}</span>
                    </div>
                    <div class="col-span-2 md:col-span-1 flex flex-col items-center justify-center bg-[#b9f1cf]/5 rounded-lg py-1 md:py-0">
                        <span class="text-[10px] text-white/50 mb-1">اختلاف قیمت</span>
                        <span class="text-xs font-black text-emerald-400">
                            {{ (calculatePrice(item, 'user') - calculatePrice(item, 'coworker')).toLocaleString() }} T
                        </span>
                    </div>
                </div>
              </div>

              <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div class="switch-card">
                  <span class="switch-label">خرید کاربر</span>
                  <label class="switch">
                    <input type="checkbox" v-model="item.user_buy_active" @change="updateStatus(item, 'user_buy_active')">
                    <span class="slider"></span>
                  </label>
                  <span class="switch-status" :class="item.user_buy_active ? 'text-[#b9f1cf]' : 'text-rose-400'">
                    {{ item.user_buy_active ? 'فعال' : 'غیرفعال' }}
                  </span>
                </div>

                <div class="switch-card">
                  <span class="switch-label">فروش کاربر</span>
                  <label class="switch">
                    <input type="checkbox" v-model="item.user_sell_active" @change="updateStatus(item, 'user_sell_active')">
                    <span class="slider"></span>
                  </label>
                  <span class="switch-status" :class="item.user_sell_active ? 'text-[#b9f1cf]' : 'text-rose-400'">
                    {{ item.user_sell_active ? 'فعال' : 'غیرفعال' }}
                  </span>
                </div>

                <div class="switch-card cw-card">
                  <span class="switch-label text-blue-300">خرید همکار</span>
                  <label class="switch">
                    <input type="checkbox" v-model="item.coworker_buy_active" @change="updateStatus(item, 'coworker_buy_active')">
                    <span class="slider slider-cw"></span>
                  </label>
                  <span class="switch-status" :class="item.coworker_buy_active ? 'text-blue-400' : 'text-rose-400'">
                    {{ item.coworker_buy_active ? 'فعال' : 'غیرفعال' }}
                  </span>
                </div>

                <div class="switch-card cw-card">
                  <span class="switch-label text-blue-300">فروش همکار</span>
                  <label class="switch">
                    <input type="checkbox" v-model="item.coworker_sell_active" @change="updateStatus(item, 'coworker_sell_active')">
                    <span class="slider slider-cw"></span>
                  </label>
                  <span class="switch-status" :class="item.coworker_sell_active ? 'text-blue-400' : 'text-rose-400'">
                    {{ item.coworker_sell_active ? 'فعال' : 'غیرفعال' }}
                  </span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      <div v-if="activeTab === 'fees'" class="animate-fade-in">
        <div class="rounded-3xl bg-gradient-to-b from-[#1a2220]/90 to-[#12181b]/90 border border-[#334D44]/50 backdrop-blur-xl p-8 shadow-2xl">
          
          <div class="flex items-center gap-3 mb-8 pb-4 border-b border-white/5">
             <div class="w-10 h-10 rounded-full bg-[#b9f1cf]/10 flex items-center justify-center text-[#b9f1cf]">
               <i class="fas fa-percent text-lg"></i>
             </div>
             <div>
               <h2 class="text-lg font-bold text-white">مدیریت درصدهای سود و کارمزد</h2>
               <p class="text-xs text-white/40 mt-1">تغییرات بلافاصله روی محاسبات سایت اعمال می‌شوند.</p>
             </div>
          </div>

          <div v-if="loadingFees" class="text-center py-10 text-white/50">در حال دریافت تنظیمات...</div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="(value, key) in form" :key="key" class="group">
              <label class="block text-xs font-bold text-[#b9f1cf]/80 mb-2 mr-1 transition-colors group-hover:text-[#b9f1cf]">{{ convertLabel(key) }}</label>
              <div class="relative">
                <input 
                  type="number" 
                  step="0.01"
                  v-model.number="form[key]"
                  class="w-full bg-[#0f1417] border border-[#334D44]/50 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#b9f1cf] focus:ring-1 focus:ring-[#b9f1cf] transition-all pl-10"
                >
                <div class="absolute left-3 top-1/2 -translate-y-1/2 text-white/20 pointer-events-none">
                   <i class="fas fa-edit"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-10 flex justify-end border-t border-white/5 pt-6">
            <button @click="saveFees" class="flex items-center gap-2 bg-[#b9f1cf] text-[#0a1f14] px-8 py-3 rounded-xl font-bold hover:brightness-110 hover:shadow-lg hover:shadow-[#b9f1cf]/20 transition-all active:scale-95">
              <i class="fas fa-save"></i>
              <span>ذخیره تغییرات</span>
            </button>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import Sidebar from '@/components/admin/SidebarAdmin.vue'

const url = process.env.VUE_APP_API_URL || ''
const activeTab = ref('status') 

const prices = ref([])
const loading = ref(true)

const form = ref({})
const loadingFees = ref(false)

const allowedItems = [
    "SekehRob", 
    "SekehNim", 
    "SekehTamam", 
    "SekehEmam", 
    "gold18", // تغییر از YekGram18 به gold18
    "mazane", 
    "SekehGerami", 
    "SekehTamamGheirBanki", 
    "SekehNimGheirBanki", 
    "SekehRobGheirBanki"
];

// فیلتر کردن لیست قیمت‌ها تا فقط موارد مجاز نمایش داده شوند
const filteredPrices = computed(() => {
    return prices.value.filter(p => allowedItems.includes(p.name))
})

onMounted(async () => {
  await fetchFees() 
  await fetchPrices()
})

async function fetchPrices() {
  loading.value = true
  try {
    const res = await axios.post(`${url}/admin/prices`)
    prices.value = res.data.data || []
  } catch (e) {
    Swal.fire({ icon: 'error', title: 'خطا', text: 'دریافت لیست ارزها ناموفق بود', background: '#1a2220', color: '#fff' })
  } finally {
    loading.value = false
  }
}

async function updateStatus(item, field) {
  try {
    await axios.post(`${url}/admin/pricesettingactive`, {
      id: item._id,
      [field]: item[field]
    })
    
    const Toast = Swal.mixin({
      toast: true, position: 'top-end', showConfirmButton: false, timer: 1500, timerProgressBar: true,
      background: '#1a2220', color: '#fff', iconColor: '#b9f1cf'
    })
    Toast.fire({ icon: 'success', title: 'ذخیره شد' })

  } catch (e) {
    item[field] = !item[field] 
    Swal.fire({ icon: 'error', title: 'خطا', text: 'ذخیره نشد', background: '#1a2220', color: '#fff' })
  }
}

async function fetchFees() {
  loadingFees.value = true
  try {
    const res = await axios.get(`${url}/admin/price`) 
    const data = res.data.data
    if (data) {
      const clone = { ...data }
      delete clone._id 
      delete clone.__v
      delete clone.createdAt
      delete clone.updatedAt
      form.value = clone
    }
  } catch (e) {
    console.log("Error fetching fees")
  } finally {
    loadingFees.value = false
  }
}

async function saveFees() {
  try {
    await axios.post(`${url}/admin/price`, form.value)
    Swal.fire({ 
        icon: 'success', 
        title: 'موفق', 
        text: 'تنظیمات کارمزد با موفقیت ذخیره شد', 
        background: '#1a2220', 
        color: '#fff',
        confirmButtonColor: '#b9f1cf',
        confirmButtonText: '<span style="color:#000">باشه</span>'
    })
    await fetchPrices() 
  } catch (e) {
    Swal.fire({ icon: 'error', title: 'خطا', text: e.response?.data?.error || 'مشکل در ذخیره', background: '#1a2220', color: '#fff' })
  }
}

function calculatePrice(item, type) {
    const basePrice = Number(item.price) || 0
    if (!form.value) return basePrice

    const name = (item.name || '').toLowerCase()
    
    if (name.includes('18') || name.includes('gold') || name.includes('gram')) {
        const percent = type === 'user' ? (form.value.gold_user_percent || 0) : (form.value.gold_cw_percent || 0)
        return Math.round(basePrice + (basePrice * percent / 100))
    }

    let feeKey = ''
    if (name.includes('emam') || name.includes('imam')) feeKey = 'imam'
    else if (name.includes('tamam') || name.includes('full')) feeKey = 'tamam'
    else if (name.includes('nim') || name.includes('half')) feeKey = 'nim'
    else if (name.includes('rob') || name.includes('quarter')) feeKey = 'rob'
    else return basePrice 

    if (name.includes('gheir') || name.includes('non')) {
        const key = type === 'user' ? `nb_${feeKey}_user_sell` : `nb_${feeKey}_cw_sell`
        return basePrice + (Number(form.value[key]) || 0)
    } else {
        const key = type === 'user' ? `${feeKey}_user_buy` : `${feeKey}_cw_sell` 
        return basePrice + (Number(form.value[key]) || 0)
    }
}

function convertLabel(key) {
  const map = {
    imam_user_buy: "خرید امامی (کاربر)",
    tamam_user_buy: "خرید تمام (کاربر)",
    nim_user_buy: "خرید نیم (کاربر)",
    rob_user_buy: "خرید ربع (کاربر)",

    imam_cw_sell: "فروش امامی (همکار)",
    tamam_cw_sell: "فروش تمام (همکار)",
    nim_cw_sell: "فروش نیم (همکار)",
    rob_cw_sell: "فروش ربع (همکار)",

    nb_tamam_user_sell: "فروش تمام غیربانکی (کاربر)",
    nb_nim_user_sell: "فروش نیم غیربانکی (کاربر)",
    nb_rob_user_sell: "فروش ربع غیربانکی (کاربر)",

    nb_tamam_cw_sell: "فروش تمام غیربانکی (همکار)",
    nb_nim_cw_sell: "فروش نیم غیربانکی (همکار)",
    nb_rob_cw_sell: "فروش ربع غیربانکی (همکار)",

    gold_user_percent: "درصد سود طلا (کاربر)",
    gold_cw_percent: "درصد سود طلا (همکار)",
  }
  return map[key] || key
}
</script>

<style scoped>
.animate-fade-in { animation: fadeIn 0.3s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.switch-card { display: flex; flex-direction: column; align-items: center; background: rgba(0, 0, 0, 0.2); padding: 16px; border-radius: 20px; border: 1px solid rgba(255, 255, 255, 0.05); transition: all 0.3s; }
.switch-card:hover { background: rgba(255, 255, 255, 0.03); border-color: rgba(255, 255, 255, 0.1); transform: translateY(-2px); }
.cw-card { background: rgba(59, 130, 246, 0.05); border-color: rgba(59, 130, 246, 0.1); }
.cw-card:hover { background: rgba(59, 130, 246, 0.08); border-color: rgba(59, 130, 246, 0.2); }

.switch-label { font-size: 11px; color: rgba(255, 255, 255, 0.6); margin-bottom: 12px; font-weight: 700; }
.switch-status { font-size: 10px; margin-top: 8px; font-weight: 800; }

.switch { position: relative; display: inline-block; width: 52px; height: 28px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #27272a; transition: .3s cubic-bezier(0.4, 0, 0.2, 1); border-radius: 34px; border: 1px solid rgba(255,255,255,0.1); }
.slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background-color: #71717a; transition: .3s cubic-bezier(0.4, 0, 0.2, 1); border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
input:checked + .slider { background-color: rgba(185, 241, 207, 0.15); border-color: #b9f1cf; }
input:checked + .slider:before { transform: translateX(24px); background-color: #b9f1cf; box-shadow: 0 0 15px rgba(185, 241, 207, 0.6); }
input:checked + .slider-cw { background-color: rgba(59, 130, 246, 0.15); border-color: #60a5fa; }
input:checked + .slider-cw:before { background-color: #60a5fa; box-shadow: 0 0 15px rgba(96, 165, 250, 0.6); }
</style>